import React, {useEffect, useRef, useState} from "react";
import mapboxgl from "mapbox-gl";
import MapElementProps from "./MapElementProps";
import './Map.scss';
import {useAppDispatch, useAppSelector} from "../../redux/hooks";
import {mapMoved, MapState, selectMapState} from "../../redux/features/map/slice";

type MapProps = {
    children: JSX.Element | JSX.Element[]
}

const Map = ({children}: MapProps) => {
    const mapContainer = useRef<HTMLDivElement>(null);
    const mapRef = useRef<mapboxgl.Map | null>(null);
    const [map, setMap] = useState<mapboxgl.Map | null>();

    const mapState: MapState = useAppSelector(selectMapState);

    useEffect(() => {
        if (!mapContainer.current || !mapState)
            return;
        if (mapRef.current)
            return; // initialize map only once

        mapRef.current = new mapboxgl.Map({
            container: mapContainer.current,
            // Start on the persisted style (default: Paper/light-v11) — a
            // hardcoded style here flashes wrong, then MapStyleControl swaps it.
            style: mapState.styleUrl,
            center: [mapState.locationAndZoom.location.longitude, mapState.locationAndZoom.location.latitude],
            zoom: mapState.locationAndZoom.zoom,
            hash: true, // sync #zoom/lat/lng to URL for shareable links
        });
        setMap(mapRef.current);
    }, [mapContainer.current]);

    const [childrenWithMap, setChildrenWithMap] = useState<JSX.Element[]>([]);

    const dispatch = useAppDispatch();
    const refreshStore = () => {
        const center = mapRef.current!.getCenter();
        const bounds = mapRef.current!.getBounds();
        dispatch(mapMoved({
            location: {
                longitude: center.lng,
                latitude: center.lat,
            },
            zoom: mapRef.current!.getZoom(),
            north: bounds.getNorth(),
            south: bounds.getSouth(),
            east: bounds.getEast(),
            west: bounds.getWest(),
        }));
    };

    useEffect(() => {
        if (map) {
            map.on('moveend', refreshStore);

            // Sync the map's initial position (possibly from the URL hash) to Redux.
            // `moveend` does not fire on initial load when `hash: true` places the map
            // at the URL hash position without animating, so we dispatch once manually.
            refreshStore();

            if (!Array.isArray(children)) {
                children = [children];
            }
            setChildrenWithMap(React.Children.map(children, child => {
                if (React.isValidElement(child)) {
                    const mapElementChild = child as React.ReactElement<MapElementProps>;
                    if (mapElementChild) {
                        return React.cloneElement(mapElementChild, {
                            map: map,
                        });
                    }
                }
                return child;
            }))
        }
    }, [map])

    return (
            <div>
                <div ref={mapContainer} className="map-container"/>
                <div>
                    {childrenWithMap}
                </div>
            </div>
    );
}

export default Map;